import { useCallback, useMemo, useState } from 'react';

import cx from 'classnames';
import arrayMutators from 'final-form-arrays';
import { pick } from 'ramda';
import { Field, Form } from 'react-final-form';
import { useDispatch } from 'react-redux';

import Button from 'components/shared/Button';
import FormGroupAdapter from 'components/shared/FormAdapters/FormDefault/FormGroup';
import RadioButton from 'components/shared/FormAdapters/FormDefault/RadioButton';
import RadioGroup from 'components/shared/FormAdapters/FormDefault/RadioButton/Group';
import { Tip } from 'components/shared/Tip';
import FormTitle from 'components/shared/Typography/FormTitle';
import { useSnackbar } from 'hooks/useSnackbar';
import useValidationSchema from 'hooks/useValidationSchema';
import i18n from 'i18n';
import { shareWithUserTypeThunk } from 'store/admin/itineraries/thunks';
import logger from 'utils/logger';

import { ITINERARIES_ITEM_TYPES } from '../../../../../../../constants';
import { SHARE_TYPE } from '../../constants';
import getValidationSchema from '../../Form/validationSchema';
import { locales } from './constants';
import { EmailTemplateForm } from './EmailTemplateForm';
import s from './index.module.css';
import Recipients from './Recipients';
import * as types from './types';

export function ShareViaEmail(props: types.ShareViaEmailProps) {
  const { itinerariesItemType, itemId, closeModal, builder, showPermissions, manageHideCosts, hideHideCosts } = props;

  const dispatch = useDispatch();
  const showSnackbar = useSnackbar();

  const [templateFormState, setTemplateFormState] = useState<types.EmailTemplateFormState>();

  const [sharingWith, setSharingWith] = useState(builder ? SHARE_TYPE.CLIENT : SHARE_TYPE.FRIEND);
  const validate = useValidationSchema(getValidationSchema(!builder));

  const getLocale = (key: keyof typeof locales) =>
    locales[key].in[itinerariesItemType]?.for?.[sharingWith] || undefined;

  const handleSubmit = useCallback(
    async (values: { clients: { email: string }[] }) => {
      const emails = values?.clients?.map((client) => client?.email)?.join(', ');
      const initialTemplateFormValues = { body: getLocale('body'), subject: getLocale('subject') };

      const handledTemplateFormValues = pick(
        ['body', 'subject'],
        templateFormState?.values ?? initialTemplateFormValues,
      );

      try {
        await dispatch(
          shareWithUserTypeThunk(
            { id: itemId, itinerariesItemType, ...values, ...handledTemplateFormValues },
            sharingWith,
          ),
        );
        showSnackbar(
          `${
            builder ? 'Email has been sent to:' : i18n.t('proposal_content.guest_portal.share.snackbar.email_sent')
          } ${emails}`,
        );
        closeModal();
      } catch (e) {
        showSnackbar('Oops! Something went wrong', true);
        logger.error(`[Share] error: '${JSON.stringify(e)}'`);
      }
    },
    [sharingWith, templateFormState],
  );

  const initialValues = useMemo(
    () => ({
      clients: [{}],
      ...(sharingWith === SHARE_TYPE.FRIEND && { guestPortalHideCosts: !manageHideCosts }),
    }),
    [],
  );

  return (
    <Form onSubmit={handleSubmit} initialValues={initialValues} validate={validate} mutators={{ ...arrayMutators }}>
      {({
        handleSubmit,
        form: {
          mutators: { push },
        },
        submitting,
        hasValidationErrors,
      }) => (
        <form onSubmit={handleSubmit} className={s.form}>
          <div
            className={cx(s.fields, {
              [s.noTabs]: itinerariesItemType === ITINERARIES_ITEM_TYPES.GUEST_PORTAL || !builder,
            })}
          >
            {builder && (
              <>
                {getLocale('info') && (
                  <Tip type="info">
                    <span>{getLocale('info')}</span>
                  </Tip>
                )}

                <div className={s.field}>
                  <FormTitle noBorder>I&apos;m sharing this with:</FormTitle>
                  <RadioGroup value={sharingWith} onChange={setSharingWith}>
                    <RadioButton
                      className={s.radioButtonWrapper}
                      radioClassNames={s.radioButton}
                      value={SHARE_TYPE.CLIENT}
                    >
                      Traveler(s)
                    </RadioButton>
                    <RadioButton
                      className={s.radioButtonWrapper}
                      radioClassNames={s.radioButton}
                      value={SHARE_TYPE.AGENT}
                    >
                      An Agent(s) from Another Company
                    </RadioButton>
                  </RadioGroup>
                </div>

                {getLocale('shareTypeInfo') && (
                  <Tip type="info" className={s.infoGray}>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: getLocale('shareTypeInfo')!,
                      }}
                    />
                  </Tip>
                )}
              </>
            )}

            <div className={cx(s.field, s.invite)}>
              <FormTitle
                noBorder
                className={builder ? undefined : s.nonBuilderFormTitle} // Don't blame me, blame the designer
                hint={builder ? undefined : i18n.t('proposal_content.guest_portal.share.add_people.description')}
              >
                {builder ? 'Invite people' : i18n.t('proposal_content.guest_portal.share.add_people.title')}
              </FormTitle>
              <Recipients push={push} name="clients" builder={builder} />
            </div>

            {builder && (
              <div className={s.field}>
                <EmailTemplateForm
                  templateFormState={templateFormState}
                  setTemplateFormState={setTemplateFormState}
                  getLocale={getLocale}
                />
              </div>
            )}
            {!builder && showPermissions && !hideHideCosts && (
              <div className={cx(s.field, s.hideCosts)}>
                <FormTitle
                  noBorder
                  className={s.nonBuilderFormTitle}
                  hint={i18n.t('proposal_content.guest_portal.share.manage_permissions.description')}
                >
                  {i18n.t('proposal_content.guest_portal.share.manage_permissions.title')}
                </FormTitle>
                <Field
                  component={FormGroupAdapter}
                  name="guestPortalHideCosts"
                  type="checkbox"
                  labelText={i18n.t('proposal_content.guest_portal.share.manage_permissions.options.hide_costs')}
                  disabled={!manageHideCosts}
                />
              </div>
            )}
          </div>
          <div className={s.footer}>
            <Button
              loading={submitting}
              htmlType="submit"
              disabled={submitting || hasValidationErrors}
              className={s.submit}
            >
              {builder ? 'Share' : i18n.t('proposal_content.guest_portal.send')}
            </Button>
          </div>
        </form>
      )}
    </Form>
  );
}
